import { useEffect, useState } from 'react';

import { authenticatedFetch } from './auth.js';
import type { RadarBriefing, RadarImportStatus, RadarSectionKind } from './shared/radar.js';

interface RadarResponse {
  briefing: RadarBriefing | null;
  status: RadarImportStatus;
  error?: { message?: string };
}

const sectionLabels: Record<RadarSectionKind, string> = {
  fact: 'Fakten und Daten',
  assessment: 'Einordnung',
  recommendation: 'Handlungsempfehlung',
};

function dateLabel(value: string | null | undefined): string {
  if (!value) return 'nicht vorhanden';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleDateString('de-CH', { dateStyle: 'medium' }) : 'unbekannt';
}

function sourceUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

export default function RadarPage() {
  const [payload, setPayload] = useState<RadarResponse | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    void authenticatedFetch('/api/radar')
      .then(async (response) => {
        const next = await response.json() as RadarResponse;
        if (!response.ok || !next.status) throw new Error(next.error?.message || 'Das Immobilien-Radar konnte nicht geladen werden.');
        if (active) setPayload(next);
      })
      .catch((loadError) => { if (active) setError(loadError instanceof Error ? loadError.message : 'Das Immobilien-Radar konnte nicht geladen werden.'); });
    return () => { active = false; };
  }, []);

  const briefing = payload?.briefing || null;
  return <main className="radar-page wrap">
    <div className="radar-page-header">
      <div>
        <p className="eyebrow">Markt &amp; Wissen</p>
        <h1>Immobilien-Radar</h1>
        <p className="radar-lead">Wöchentliches Marktbriefing mit Trends und Entwicklungen für unsere Immobilienberatung.</p>
      </div>
      <a className="secondary-button radar-back-link" href="/dashboard">← Zum Dashboard</a>
    </div>

    {error && <div className="notice notice--error" role="alert"><p>{error}</p></div>}
    {payload?.status.stale && <div className="notice notice--warning" role="status"><p><strong>Datenstand prüfen</strong>Der letzte gültige Radar-Inhalt ist älter als zehn Tage oder noch nicht vorhanden. Ein Seitenaufruf aktualisiert die Daten nicht automatisch.</p></div>}
    {!payload && !error && <div className="notice notice--info"><p>Briefing wird geladen …</p></div>}
    {payload && !briefing && !error && <div className="notice notice--info"><p>Noch kein Radar-Briefing ist mit dem Portal verbunden. Die bestehende wöchentliche Dot-Aktualisierung muss einmalig an den sicheren Importweg angeschlossen werden.</p></div>}
    {briefing && <>
      <section className="radar-briefing-head panel-card">
        <div><p className="eyebrow">Wochenbriefing</p><h2>{briefing.title}</h2><p className="radar-meta">Berichtszeitraum: {briefing.reportPeriod} · Datenstand: {dateLabel(briefing.dataAsOf)}</p></div>
        <div className="radar-dates"><span>Veröffentlicht {dateLabel(briefing.publishedAt)}</span><span>Importiert {dateLabel(briefing.importedAt)}</span></div>
      </section>

      <section className="radar-section radar-highlights"><div className="radar-section-heading"><p className="eyebrow">Kurzüberblick</p><h2>Die wichtigsten Entwicklungen</h2></div><div className="radar-highlight-grid">{briefing.highlights.slice(0, 3).map((highlight, index) => <article className="radar-highlight" key={`${index}-${highlight}`}><span>{String(index + 1).padStart(2, '0')}</span><p>{highlight}</p></article>)}</div></section>

      {briefing.metrics.length > 0 && <section className="radar-section"><div className="radar-section-heading"><p className="eyebrow">Kennzahlen</p><h2>Aktuelle Werte</h2></div><div className="radar-metric-grid">{briefing.metrics.map((metric) => <article className="radar-metric" key={`${metric.label}-${metric.value}`}><span>{metric.label}</span><strong>{metric.value}</strong>{metric.context && <small>{metric.context}</small>}</article>)}</div></section>}

      <section className="radar-section radar-content-grid"><div className="radar-section-heading"><p className="eyebrow">Einordnung</p><h2>Markt und Region</h2></div><div className="radar-sections">{briefing.sections.map((section) => <article className={`radar-content-card radar-content-card--${section.kind}`} key={section.id}><span className="radar-kind">{sectionLabels[section.kind]}</span><h3>{section.title}</h3><p>{section.body}</p>{section.items.length > 0 && <ul>{section.items.map((item) => <li key={item}>{item}</li>)}</ul>}</article>)}</div></section>

      <section className="radar-section radar-sources"><div className="radar-section-heading"><p className="eyebrow">Nachvollziehbarkeit</p><h2>Quellen</h2></div><div className="radar-source-list">{briefing.sources.map((source) => { const href = sourceUrl(source.url); return <div className="radar-source" key={`${source.url}-${source.publishedAt}`}><strong>{source.title}</strong><span>{source.publisher || 'Quelle'} · {dateLabel(source.publishedAt)}</span>{href ? <a href={href} target="_blank" rel="noreferrer">Quelle öffnen</a> : <span>Link nicht verfügbar</span>}</div>; })}</div></section>
    </>}
  </main>;
}
